// ============================================================================
// WizardContainerInner Component (View)
// ============================================================================

import { Box, IconButton, Typography, Chip } from "@mui/material";
import { Close } from "@mui/icons-material";
import { StepProgress, WizardNavigation } from "../components";
import { WIZARD_STEP_TITLES } from "./WizardContainer.types";
import {
	FileValidation,
	PropertyMapping,
	GeoTypeSelection,
	OverlappingBoundaries,
	DuplicateBoundaries,
	OverlappingPoints,
	OverlappingZones,
	UnmatchedFeatures,
	BranchConfiguration,
	DealConfiguration,
	SalespersonAssignment,
	MissingInvalidData,
	Summary,
} from "../steps";
import type { WizardContainerInnerProps } from "./WizardContainerInner.types";
import { useWizardContainerInnerViewModel } from "./WizardContainerInner.vm";
import "./WizardContainerInner.css";

/**
 * Inner container component that uses wizard context
 */
export function WizardContainerInner(props: WizardContainerInnerProps) {
	const { onCancel, onStartOver, company, allDeals, salespersons, branches, regions, growers, user } = props;
	const vm = useWizardContainerInnerViewModel(props);

	// Render current step content
	const renderStepContent = () => {
		switch (vm.currentStep) {
			// Configuration phase steps
			case "file-validation":
				return (
					<FileValidation
						warnings={vm.configuration.warnings}
						uploadedFiles={vm.configuration.uploadedFiles}
						onPrevious={vm.handlePrevious}
						onNext={vm.handleNext}
						onAddFiles={vm.handleAddFiles}
						onClear={vm.handleClearFiles}
					/>
				);
			case "property-mapping":
				return (
					<PropertyMapping
						geodataGroups={vm.configuration.geodataGroups}
						propertyMappings={vm.configuration.propertyMappings}
						onMappingChange={vm.handleMappingChange}
						onBatchMappingChange={vm.handleBatchMappingChange}
						onApplyGlobalMapping={vm.handleApplyGlobalMapping}
						onPrevious={vm.handlePrevious}
						onNext={vm.handleNext}
						company={company}
					/>
				);
			case "geo-type-selection":
				return (
					<GeoTypeSelection
						geodataGroups={vm.configuration.geodataGroups}
						geoTypeSelections={vm.configuration.geoTypeSelections}
						excludedGroupIds={vm.configuration.excludedGroupIds}
						selectedGroupIdsForMap={vm.configuration.selectedGroupIdsForMap}
						propertyMappings={vm.configuration.propertyMappings}
						configuration={vm.configuration}
						onGeoTypeChange={vm.handleGeoTypeChange}
						onApplyToAll={vm.handleApplyToAllGeoType}
						onApplyToSelected={vm.handleApplyToSelectedGeoType}
						onExcludeGroup={vm.handleExcludeGroup}
						onIncludeGroup={vm.handleIncludeGroup}
						onMapSelectionChange={vm.handleMapSelectionChange}
						onPrevious={vm.handlePrevious}
						onNext={vm.handleCompleteConfiguration}
						company={company}
						allDeals={allDeals}
						salespersons={salespersons}
						branches={branches}
						growers={growers}
						user={user}
					/>
				);
			// Validation phase steps
			case "overlapping-boundaries":
				return (
					<OverlappingBoundaries
						issues={vm.issues.overlaps}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteJob={vm.handleDeleteJob}
					/>
				);
			case "duplicate-boundaries":
				return (
					<DuplicateBoundaries
						issues={vm.issues.duplicateBoundaries}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteJob={vm.handleDeleteJob}
						onMergeJobs={vm.handleMergeJobs}
						salespersons={salespersons}
						branches={branches}
						deals={allDeals}
					/>
				);
			case "overlapping-points":
				return (
					<OverlappingPoints
						issues={vm.issues.overlappingPoints}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteFeature={vm.handleDeleteFeature}
						onDeleteFeatures={vm.handleDeleteFeatures}
					/>
				);
			case "overlapping-zones":
				return (
					<OverlappingZones
						issues={vm.issues.overlappingZones}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteFeature={vm.handleDeleteFeature}
						onDeleteFeatures={vm.handleDeleteFeatures}
					/>
				);
			case "unmatched-features":
				return (
					<UnmatchedFeatures
						issues={vm.issues.unmatched}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onDeleteJob={vm.handleDeleteJob}
						onAssignToJob={vm.handleAssignToJob}
						onSplitFeatures={vm.handleSplitFeatures}
						onUploadBoundary={vm.handleUploadBoundary}
					/>
				);
			case "branch-configuration":
				return (
					<BranchConfiguration
						jobs={vm.jobs}
						branches={branches || []}
						regions={regions}
						branchAssignments={vm.branchAssignments}
						onAssignGrowerToBranch={vm.handleAssignGrowerToBranch}
					/>
				);
			case "deal-configuration":
				return (
					<DealConfiguration
						jobs={vm.jobs}
						allDeals={allDeals || []}
						dealAssignments={vm.dealAssignments}
						onAssignGrowerToDeal={vm.handleAssignGrowerToDeal}
					/>
				);
			case "salesperson-assignment":
				return (
					<SalespersonAssignment
						jobs={vm.jobs}
						salespersons={salespersons || []}
						salespersonAssignments={vm.salespersonAssignments}
						onAssignGrowerToSalesperson={vm.handleAssignGrowerToSalesperson}
						onAddSalesperson={vm.handleAddSalesperson}
						isSalespersonRequired={vm.isSalespersonRequired}
					/>
				);
			case "missing-invalid-data":
				return (
					<MissingInvalidData
						issues={vm.issues.invalidData}
						currentIssueIndex={vm.currentIssueIndex}
						skippedIssueIds={vm.skippedIssueIds}
						onUpdateJob={vm.handleUpdateJob}
						onDeleteJob={vm.handleDeleteJob}
						onRegisterActions={vm.setInvalidDataActions}
					/>
				);
			case "summary":
				return (
					<Summary
						jobs={vm.jobs}
						issues={vm.issues}
						skippedIssueIds={vm.skippedIssueIds}
						onNavigateToIssue={vm.handleNavigateToIssue}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div className="wizard-container-inner">
			{/* Body: sidebar + main area */}
			<div className="wizard-body">
				<aside className="wizard-sidebar">
					<StepProgress
						currentStep={vm.currentStep}
						visibleSteps={vm.visibleSteps}
						completedSteps={vm.completedSteps}
						resolvedSteps={vm.resolvedSteps}
						visitedSteps={vm.visitedSteps}
						stepIssueCounts={vm.stepIssueCounts}
						onStepClick={vm.handleGoToStep}
					/>
				</aside>

				<div className="wizard-main">
					{/* Header: step title + job count + close */}
					<div className="wizard-header">
						<Typography variant="subtitle1" fontWeight={600}>
							{WIZARD_STEP_TITLES[vm.currentStep]}
						</Typography>
						<div className="wizard-header-spacer" />
						{vm.totalJobCount > 0 && (
							<Chip
								label={`${vm.totalJobCount} ${vm.totalJobCount === 1 ? "Job" : "Jobs"}`}
								size="small"
								color="primary"
								variant="outlined"
								className="jobs-chip"
							/>
						)}
						<IconButton aria-label="close" onClick={onCancel} className="close-button">
							<Close />
						</IconButton>
					</div>

					{/* Content */}
					<div className="wizard-content">
						{vm.isValidating ? (
							<Box className="validating-state">
								<Typography>Validating jobs...</Typography>
							</Box>
						) : vm.validationError ? (
							<Box className="error-state">
								<Typography>Error: {vm.validationError}</Typography>
							</Box>
						) : (
							renderStepContent()
						)}
					</div>
				</div>
			</div>

			{/* Footer: Navigation */}
			<div className="wizard-footer">
				<WizardNavigation
					isFirstStep={vm.isFirstStep}
					isLastStep={vm.isLastStep}
					canProceed={vm.canClickNext}
					canClickPrevious={vm.canClickPrevious}
					disabledReason={vm.disabledReason}
					onPrevious={vm.handlePrevious}
					onNext={vm.handleNext}
					onFinish={vm.handleFinish}
					// Issue props (for Skip buttons)
					issueCount={vm.currentStepIssues.length}
					unskippedCount={vm.skippableUnskippedCount}
					isCurrentIssueSkipped={vm.isCurrentIssueSkipped}
					onSkipIssue={vm.currentIssue && vm.canSkipCurrentIssue ? vm.handleSkipIssue : undefined}
					onSkipAllIssues={vm.handleSkipAllIssues}
					// MissingInvalidData step actions
					onDeleteJob={vm.currentStep === "missing-invalid-data" ? vm.invalidDataActions?.requestDelete : undefined}
					onSaveChanges={vm.currentStep === "missing-invalid-data" ? vm.invalidDataActions?.requestSave : undefined}
					// Start Over button (only shown when onStartOver prop is provided)
					onStartOver={onStartOver ? vm.handleStartOver : undefined}
					// GeoTypeSelection status chips
					geoTypeStatus={vm.geoTypeStatus}
				/>
			</div>
		</div>
	);
}
